import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { PaperPlaneTiltIcon } from '@phosphor-icons/react/dist/csr/PaperPlaneTilt';
import { XIcon } from '@phosphor-icons/react/dist/csr/X';

import { Button } from '../../design-system/primitives/Button/Button';
import { Dialog } from '../../design-system/composites/Dialog/Dialog';
import { Select, type SelectOption } from '../../design-system/primitives/Select/Select';
import {
  ApiError,
  NETWORK_ERROR_CODE,
  PAGE_EXPIRED_CODE,
  TOO_MANY_REQUESTS_CODE,
} from '../../lib/api/client';
import { smsApi, type SmsMessageRow, type SmsTemplate } from '../../lib/api/sms';
import { mapDetails } from '../reference/referenceFormHelpers';
import styles from './SendMessageDialog.module.css';

const FORBIDDEN_CODE = 'forbidden';

/**
 * The send picker — `POST /cases/{case}/messages`, gated on `sms.send` (this
 * dialog is never even mounted with `open` for a session without it;
 * `CaseMessages` does not render the trigger button either — belt and
 * braces, the identical split `CaseUpdateDialog`'s own docblock states for
 * `case.update`).
 *
 * **`kind="form"`** — this is a form the agent came to fill in, titled
 * "Send a message"; a form dialog names the task, and `Dialog`'s own
 * development guard would flag a `'confirm'`-shaped title here for not
 * asking a question, which "Send a message" rightly does not (Task 8
 * ruling 2).
 *
 * On a successful send, this component hands the returned row straight to
 * `onSent` and does not itself re-fetch anything — `CaseMessages` prepends
 * it (Task 8 ruling 3): the API already answered with the row that was
 * created, in status `queued`, and a follow-up `GET` would only be asking a
 * question already answered.
 *
 * ## The chosen template's body is previewed here (fix round 1)
 *
 * `SmsTemplateResource` exposes `body`/`variables` SPECIFICALLY so a send
 * screen can show what is about to go to a patient (that resource's own
 * docblock) — an agent choosing a name off a `Select` was otherwise sending
 * Arabic text they had never read. The preview is `dir="rtl"` and
 * `lang="ar"` so it reads correctly sitting inside this (English-chrome)
 * dialog, `white-space: pre-wrap` so the real `\r\n` line breaks the
 * seeded bodies carry (`SmsTemplateSeeder`'s own comments) render as they
 * will in the handset, not collapsed to one line. It is still the RAW
 * template — `{{placeholders}}` intact, rendered against a case only at
 * send time — so a one-line notice underneath says so, rather than letting
 * an agent believe the braces themselves will reach the patient.
 *
 * ## `error.details` is read BEFORE the generic fallback (fix round 1)
 *
 * `StoreCaseMessageRequest`'s one rule (`Rule::exists('sms_templates',
 * 'code')->where('is_active', true)`) answers a retired or unknown
 * `template_code` with a `422` naming exactly that field. The first pass
 * of this dialog fell straight to `describeFailure`'s generic
 * `cases.actionFailed` for a `422` the way it does for a genuine `403`/
 * `429`/`419` — losing the one piece of detail the API went out of its
 * way to send. `CaseUpdateDialog` (the sibling this dialog otherwise
 * mirrors) checks `Object.keys(error.details).length > 0` FIRST; this
 * dialog now does too, mapping `template_code`'s message onto the
 * `Select` itself via `mapDetails`, the identical helper.
 */

export interface SendMessageDialogProps {
  open: boolean;
  caseUuid: string;
  templates: readonly SmsTemplate[];
  onClose: () => void;
  onSent: (row: SmsMessageRow) => void;
}

export function SendMessageDialog({ open, caseUuid, templates, onClose, onSent }: SendMessageDialogProps) {
  const { t } = useTranslation();

  const [templateCode, setTemplateCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const inFlight = useRef(false);

  useEffect(() => {
    if (!open) return;
    setTemplateCode(templates[0]?.code ?? '');
    setFailure(null);
    setFieldErrors({});
  }, [open, templates]);

  function describeFailure(error: ApiError): string {
    switch (error.code) {
      case FORBIDDEN_CODE:
        return t('cases.notPermitted');
      case TOO_MANY_REQUESTS_CODE:
        return t('cases.throttled');
      case PAGE_EXPIRED_CODE:
        return t('auth.sessionExpired');
      case NETWORK_ERROR_CODE:
        return t('auth.unreachable');
      default:
        return t('cases.actionFailed');
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (inFlight.current || templateCode === '') return;

    inFlight.current = true;
    setSubmitting(true);
    setFailure(null);
    setFieldErrors({});

    try {
      const sent = await smsApi.send(caseUuid, templateCode);
      onSent(sent);
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      // `CaseUpdateDialog`'s own split: a `422` naming a field is shown on
      // that field, never downgraded to the generic fallback below it.
      if (Object.keys(error.details).length > 0) {
        setFieldErrors(mapDetails(error.details));
      } else {
        setFailure(describeFailure(error));
      }
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  }

  const options: readonly SelectOption[] = templates.map((template) => ({
    value: template.code,
    label: template.name,
  }));

  const selectedTemplate = templates.find((template) => template.code === templateCode) ?? null;

  return (
    <Dialog open={open} kind="form" onClose={onClose} title={t('sms.sendTitle')}>
      <form
        className={styles['form']}
        method="post"
        noValidate
        onSubmit={(event) => {
          void handleSubmit(event);
        }}
      >
        <div className={styles['failure']} role="alert" aria-atomic="true">
          {failure}
        </div>

        <Select
          label={t('sms.template')}
          width="compact"
          required
          options={options}
          value={templateCode}
          error={fieldErrors['template_code']}
          onChange={(event) => {
            const { value } = event.target;
            setTemplateCode(value);
          }}
        />

        {selectedTemplate === null ? null : (
          <div className={styles['preview']}>
            <p className={styles['previewLabel']}>{t('sms.bodyPreview')}</p>
            <p className={styles['previewBody']} dir="rtl" lang="ar">
              {selectedTemplate.body}
            </p>
            <p className={styles['previewNote']}>{t('sms.placeholderNotice')}</p>
          </div>
        )}

        <div className={styles['actions']}>
          <Button type="submit" loading={submitting} icon={<PaperPlaneTiltIcon size={16} />}>
            {t('sms.send')}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose} icon={<XIcon size={16} />}>
            {t('cases.cancel')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
